<template>
  <div class="home-container">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">ArcadeJoy - Free Online Classic Games Collection</h1>
        <p class="hero-subtitle">Experience the joy of classic arcade games, reimagined for the modern web</p>
        <p class="hero-description">
          At ArcadeJoy, we're dedicated to bringing you the purest gaming experience. Our game collection features classic titles like Tetris, Snake, Bird Flight, and Alphabet Dash, 
          each carefully optimized to run perfectly on both PC and mobile devices. No downloads required, no registration needed - just click and play!
        </p>
        <div class="hero-buttons">
          <button @click="scrollToGames" class="cta-button primary">Start Playing</button>
          <button @click="scrollToFeatures" class="cta-button secondary">Learn More</button>
          <router-link to="/game-guide" class="cta-button guide">Game Guide</router-link>
        </div>
      </div>
      <div class="hero-animation">
        <div class="floating-icons">
          <div class="icon tetris-icon">🧩</div>
          <div class="icon snake-icon">🐍</div>
          <div class="icon bird-icon">🐦</div>
          <div class="icon alphabet-icon">🔤</div>
        </div>
      </div>
    </section>

    <!-- Ad Banner - Top -->
    <ad-banner 
      ad-slot="1234567890"
      ad-format="horizontal"
      width="100%"
      height="90px"
    />

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="container">
        <h2 class="section-title">Why Choose ArcadeJoy?</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🎮</div>
            <h3>Classic Gaming Experience</h3>
            <p>We've carefully recreated the most beloved classic arcade games, preserving their authentic gameplay mechanics while adding modern visual effects and smooth controls. Each game has been tested and optimized for hundreds of hours.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📱</div>
            <h3>Perfect Mobile Adaptation</h3>
            <p>All games are perfectly optimized for mobile devices, supporting touch controls and virtual buttons. Whether you're using a phone, tablet, or computer, you'll enjoy a consistent high-quality gaming experience. Responsive design ensures perfect display on any screen size.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🚀</div>
            <h3>Instant Loading</h3>
            <p>Built with the latest web technologies, our games load incredibly fast. No downloads or installations required - just click and play. We use advanced caching techniques and code optimization to ensure games start quickly in any network environment.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔒</div>
            <h3>Safe & Secure</h3>
            <p>We value user privacy and data security. All game data is stored locally and never uploaded to servers. We follow the strictest privacy protection standards to ensure your gaming experience is both safe and private.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🎯</div>
            <h3>Precise Control</h3>
            <p>Each game has been carefully calibrated to ensure responsive and precise controls. We've optimized the physics engines and collision detection so every action you take gets accurate feedback.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🏆</div>
            <h3>Achievement System</h3>
            <p>Built-in comprehensive scoring and achievement system to record your best scores and challenge higher achievements. Local storage ensures your game records are never lost, so you can track your progress anytime.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Games Section -->
    <section id="games" class="games-section">
      <div class="container">
        <h2 class="section-title">Featured Game Collection</h2>
        <p class="section-description">
          We've carefully selected and recreated four of the most popular classic games, each with its unique charm and challenges.
          Whether you're a nostalgic player or a newcomer, you'll find the perfect game for yourself here.
        </p>
        
        <div class="games-grid">
          <!-- 俄罗斯方块游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail tetris-thumbnail">
              <div class="tetris-blocks">
                <div class="tetris-block" style="background-color: #00bcd4;"></div>
                <div class="tetris-block" style="background-color: #2962ff;"></div>
                <div class="tetris-block" style="background-color: #ff9800;"></div>
                <div class="tetris-block" style="background-color: #ffeb3b;"></div>
              </div>
              <div class="game-overlay">
                <h3>BlockBeat</h3>
                <p>Classic Tetris Game</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Tetris - The Eternal Classic</h4>
                <p>
                  Tetris is one of the world's most popular puzzle games. In this game, you need to control falling blocks,
                  rotating and moving them to fit perfectly together, clearing complete lines to score points. The game pace
                  accelerates as levels increase, testing your reaction speed and spatial imagination.
                </p>
                <ul class="game-features">
                  <li>7 classic block shapes</li>
                  <li>Smooth rotation and movement controls</li>
                  <li>Progressive difficulty increase</li>
                  <li>Local high score records</li>
                </ul>
              </div>
              <router-link to="/tetris" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 字母冲刺游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail alphabet-dash-thumbnail">
              <div class="alphabet-preview">
                <span>A</span>
                <span>B</span>
                <span>C</span>
              </div>
              <div class="game-overlay">
                <h3>Alphabet Dash</h3>
                <p>Typing Speed Game</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Alphabet Dash - Typing Speed Challenge</h4>
                <p>
                  Alphabet Dash is a game designed specifically to improve typing speed and accuracy. You need to quickly input
                  all 26 English letters according to prompts, challenging your reaction speed and keyboard proficiency.
                  The game offers multiple modes including classic mode, chaos mode, and timed challenges.
                </p>
                <ul class="game-features">
                  <li>Multiple game mode options</li>
                  <li>Real-time speed and accuracy statistics</li>
                  <li>Cute character selection</li>
                  <li>Mobile virtual keyboard support</li>
                </ul>
              </div>
              <router-link to="/alphabet-dash" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 小鸟飞行游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail bird-flight-thumbnail">
              <div class="bird-preview">
                <div class="preview-bird"></div>
                <div class="preview-obstacle"></div>
              </div>
              <div class="game-overlay">
                <h3>Bird Flight</h3>
                <p>Flying & Dodging Game</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Bird Flight - Test of Skill and Reaction</h4>
                <p>
                  Control a cute bird flying through obstacle-filled skies. Tap the screen to make the bird fly up, release to let it fall.
                  You need to precisely control flight altitude, avoid green pipe obstacles, and collect golden stars for extra points.
                  This is a game that tests patience and skill.
                </p>
                <ul class="game-features">
                  <li>Simple one-tap controls</li>
                  <li>Beautiful sky backgrounds and animations</li>
                  <li>Star collection system</li>
                  <li>Life system increases error tolerance</li>
                </ul>
              </div>
              <router-link to="/bird-flight" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 贪吃蛇游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail snake-game-thumbnail">
              <div class="snake-preview">
                <div class="preview-snake-head"></div>
                <div class="preview-snake-body"></div>
                <div class="preview-food"></div>
              </div>
              <div class="game-overlay">
                <h3>Snake Puzzle</h3>
                <p>Classic Snake Game</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Snake Game - Strategy and Growth</h4>
                <p>
                  The classic snake game where you control a snake moving within the game area, eating food to grow your body. As the snake gets longer,
                  the difficulty gradually increases, requiring more careful route planning to avoid hitting walls or your own body.
                  This is a timeless strategy game.
                </p>
                <ul class="game-features">
                  <li>Classic game mechanics</li>
                  <li>Smooth directional controls</li>
                  <li>Progressive speed increases</li>
                  <li>Beautiful visual effects</li>
                </ul>
              </div>
              <router-link to="/snake-game" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 反应速度测试游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail reaction-time-thumbnail">
              <div class="reaction-preview">
                <div class="preview-circle waiting"></div>
                <div class="preview-circle ready"></div>
                <div class="preview-circle go"></div>
              </div>
              <div class="game-overlay">
                <h3>Reaction Time</h3>
                <p>Speed Challenge</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Reaction Time Test - Lightning Fast Reflexes Training Game</h4>
                <p>
                  Test your reflexes and reaction speed with this exciting online reaction time game! Perfect for gamers, athletes, and anyone wanting to improve their response time. 
                  Wait for the green signal and click as fast as you can. Compare your reaction times with friends and see who has the fastest reflexes. 
                  This free browser-based reflex test is perfect for competitive gaming, esports training, and cognitive skill improvement.
                </p>
                <ul class="game-features">
                  <li>Free online reaction time test</li>
                  <li>Esports reflex training tool</li>
                  <li>Multiple difficulty levels</li>
                  <li>Detailed performance analysis</li>
                  <li>Social sharing & leaderboards</li>
                  <li>Personal best tracking system</li>
                  <li>Mobile-friendly touch controls</li>
                  <li>No download required</li>
                </ul>
              </div>
              <router-link to="/reaction-time" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 数字记忆挑战游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail memory-numbers-thumbnail">
              <div class="memory-preview">
                <div class="preview-number">1</div>
                <div class="preview-number">4</div>
                <div class="preview-number">7</div>
                <div class="preview-number">2</div>
              </div>
              <div class="game-overlay">
                <h3>Memory Numbers</h3>
                <p>Brain Challenge</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Memory Numbers Challenge - Brain Training Memory Game</h4>
                <p>
                  Challenge your memory with this addictive number sequence memory game! Perfect for brain training, cognitive enhancement, and memory improvement. 
                  Watch the numbers appear, then repeat them back in the correct order. Each level adds one more number to remember. 
                  This free online memory test is scientifically designed to boost your working memory, concentration, and mental agility. How far can your memory take you?
                </p>
                <ul class="game-features">
                  <li>Free brain training memory game</li>
                  <li>Scientific memory improvement</li>
                  <li>Progressive difficulty levels</li>
                  <li>Multiple speed settings</li>
                  <li>Memory performance analysis</li>
                  <li>Cognitive enhancement tool</li>
                  <li>Working memory training</li>
                  <li>Social sharing & achievements</li>
                </ul>
              </div>
              <router-link to="/memory-numbers" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 打字竞速游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail typing-race-thumbnail">
              <div class="typing-preview">
                <div class="preview-text">The quick brown fox...</div>
                <div class="preview-cursor">|</div>
              </div>
              <div class="game-overlay">
                <h3>Typing Race</h3>
                <p>Speed & Accuracy</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Typing Race - Free Online Typing Speed Test & Training</h4>
                <p>
                  Improve your typing speed and accuracy with this comprehensive online typing test! Perfect for students, professionals, and anyone wanting to boost their keyboard skills.
                  Choose from words, sentences, quotes, or code snippets. Track your WPM (Words Per Minute) and accuracy percentage in real-time.
                  This free typing tutor helps you master touch typing, increase productivity, and excel in digital communication.
                </p>
                <ul class="game-features">
                  <li>Free online typing speed test</li>
                  <li>Touch typing training course</li>
                  <li>Multiple text modes & languages</li>
                  <li>Real-time WPM tracking</li>
                  <li>Accuracy measurement & analysis</li>
                  <li>Performance analytics dashboard</li>
                  <li>Keyboard skills improvement</li>
                  <li>Professional typing certification</li>
                </ul>
              </div>
              <router-link to="/typing-race" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 数独解谜游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail sudoku-thumbnail">
              <div class="sudoku-preview">
                <div class="preview-grid">
                  <div class="preview-cell">5</div>
                  <div class="preview-cell"></div>
                  <div class="preview-cell">8</div>
                  <div class="preview-cell"></div>
                  <div class="preview-cell">7</div>
                  <div class="preview-cell">2</div>
                  <div class="preview-cell">1</div>
                  <div class="preview-cell"></div>
                  <div class="preview-cell">9</div>
                </div>
              </div>
              <div class="game-overlay">
                <h3>Sudoku Puzzle</h3>
                <p>Logic Challenge</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Sudoku Puzzle - Free Online Classic Logic Brain Game</h4>
                <p>
                  Exercise your logical thinking with the world's most popular number puzzle game! This free online Sudoku offers unlimited puzzles with four difficulty levels.
                  Fill the 9×9 grid so each row, column, and 3×3 box contains all digits 1-9. Perfect for brain training, logical reasoning, and mental exercise.
                  Features advanced note-taking, hint system, and progress tracking. Challenge yourself with daily Sudoku puzzles!
                </p>
                <ul class="game-features">
                  <li>Free unlimited Sudoku puzzles</li>
                  <li>Four difficulty levels (Easy to Expert)</li>
                  <li>Advanced note-taking system</li>
                  <li>Smart hint system available</li>
                  <li>Time tracking & personal records</li>
                  <li>Daily Sudoku challenges</li>
                  <li>Brain training & logic improvement</li>
                  <li>Mobile-friendly interface</li>
                </ul>
              </div>
              <router-link to="/sudoku" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 颜色匹配游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail color-match-thumbnail">
              <div class="color-preview">
                <div class="preview-target"></div>
                <div class="preview-options">
                  <div class="preview-option color1"></div>
                  <div class="preview-option color2"></div>
                  <div class="preview-option color3"></div>
                  <div class="preview-option color4"></div>
                </div>
              </div>
              <div class="game-overlay">
                <h3>Color Match</h3>
                <p>Visual Challenge</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>Color Match - Free Color Vision Test & Perception Training Game</h4>
                <p>
                  Challenge your visual perception and color recognition skills with this scientifically-designed color matching game! Perfect for artists, designers, and anyone wanting to improve their color vision.
                  Match the target color from multiple similar options across different game modes and difficulty levels. This free color blindness test and vision training tool helps enhance your visual accuracy and color discrimination abilities.
                </p>
                <ul class="game-features">
                  <li>Free color vision test & training</li>
                  <li>Multiple game modes & difficulties</li>
                  <li>Color perception assessment</li>
                  <li>Visual accuracy training tool</li>
                  <li>Color blindness screening</li>
                  <li>Speed-based scoring system</li>
                  <li>Artist & designer tool</li>
                  <li>Scientific color discrimination</li>
                </ul>
              </div>
              <router-link to="/color-match" class="play-button">Play Now</router-link>
            </div>
          </div>

          <!-- 2048数字游戏卡片 -->
          <div class="game-card">
            <div class="game-thumbnail game-2048-thumbnail">
              <div class="game-2048-preview">
                <div class="preview-board">
                  <div class="preview-tile tile-2">2</div>
                  <div class="preview-tile tile-4">4</div>
                  <div class="preview-tile tile-8">8</div>
                  <div class="preview-tile tile-16">16</div>
                  <div class="preview-tile tile-32">32</div>
                  <div class="preview-tile tile-64">64</div>
                  <div class="preview-tile tile-128">128</div>
                  <div class="preview-tile tile-256">256</div>
                  <div class="preview-tile tile-512">512</div>
                  <div class="preview-tile tile-1024">1K</div>
                  <div class="preview-tile tile-2048 highlight">2048</div>
                  <div class="preview-tile empty"></div>
                  <div class="preview-tile empty"></div>
                  <div class="preview-tile empty"></div>
                  <div class="preview-tile empty"></div>
                  <div class="preview-tile empty"></div>
                </div>
              </div>
              <div class="game-overlay">
                <h3>2048</h3>
                <p>Number Puzzle</p>
              </div>
            </div>
            <div class="game-info">
              <div class="game-description">
                <h4>2048 Game - Free Online Classic Number Puzzle & Brain Teaser</h4>
                <p>
                  Play the world-famous 2048 number puzzle game online for free! Slide tiles to combine numbers and reach the legendary 2048 tile in this addictive math puzzle.
                  This strategic brain teaser challenges your planning skills and mathematical thinking. Swipe in four directions to move tiles, merge identical numbers, and achieve the highest score possible.
                  Perfect for puzzle lovers, math enthusiasts, and anyone seeking a mental challenge!
                </p>
                <ul class="game-features">
                  <li>Free online 2048 puzzle game</li>
                  <li>Classic & Endless game modes</li>
                  <li>Smooth touch & swipe controls</li>
                  <li>Achievement & milestone system</li>
                  <li>Score tracking & social sharing</li>
                  <li>Strategic brain training</li>
                  <li>Mathematical puzzle challenge</li>
                  <li>Mobile-optimized gameplay</li>
                </ul>
              </div>
              <router-link to="/2048" class="play-button">Play Now</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Ad Banner - Middle -->
    <ad-banner 
      ad-slot="0987654321"
      ad-format="rectangle"
      width="100%"
      height="250px"
    />

    <!-- How to Play Section -->
    <section class="how-to-play-section">
      <div class="container">
        <h2 class="section-title">How to Start Playing</h2>
        <div class="steps-grid">
          <div class="step-card">
            <div class="step-number">1</div>
            <h3>Choose a Game</h3>
            <p>Browse our game collection and select the game that interests you. Each game has detailed descriptions and feature explanations.</p>
          </div>
          <div class="step-card">
            <div class="step-number">2</div>
            <h3>Click Start</h3>
            <p>Click the "Play Now" button and the game will load within seconds. No downloads or software installations required.</p>
          </div>
          <div class="step-card">
            <div class="step-number">3</div>
            <h3>Enjoy Gaming</h3>
            <p>Use keyboard or touch controls to play the game. Each game has detailed operation instructions and tips.</p>
          </div>
          <div class="step-card">
            <div class="step-number">4</div>
            <h3>Challenge High Scores</h3>
            <p>Work to beat your high score records, compete with yourself, and continuously improve your gaming skills.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <div class="faq-grid">
          <div class="faq-item">
            <h3>🎮 Are the games completely free?</h3>
            <p>Yes, all games on ArcadeJoy are completely free. We don't charge any fees, and there are no hidden paid content or subscriptions.</p>
          </div>
          <div class="faq-item">
            <h3>📱 Do you support mobile devices?</h3>
            <p>Absolutely! All our games are perfectly optimized for mobile devices, including phones and tablets. You can use touch controls or virtual buttons to play.</p>
          </div>
          <div class="faq-item">
            <h3>💾 Will game data be saved?</h3>
            <p>Yes, your high scores and game settings are automatically saved in your local browser and will be restored on your next visit.</p>
          </div>
          <div class="faq-item">
            <h3>🌐 Do I need an internet connection?</h3>
            <p>An internet connection is required for initial game loading, but once loaded, most game features can be used offline.</p>
          </div>
          <div class="faq-item">
            <h3>🔧 What if I encounter problems?</h3>
            <p>If you encounter any technical issues or have suggestions, please contact us through our contact page. We'll respond and resolve issues as quickly as possible.</p>
          </div>
          <div class="faq-item">
            <h3>🎯 Will you add new games?</h3>
            <p>We regularly add new classic games and improve existing ones. Stay tuned for our updates to experience more exciting content.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section class="about-section">
      <div class="container">
        <h2 class="section-title">About ArcadeJoy</h2>
        <div class="about-content">
          <p>
            ArcadeJoy was founded in 2025 as an online platform dedicated to classic arcade games. Our mission is to bring those classic gaming experiences to a new generation of players,
            while providing nostalgic gamers with a place to relive their childhood memories.
          </p>
          <p>
            Our team consists of experienced game developers and designers who have a deep understanding and love for classic games.
            We use the latest web technologies to recreate these classic games, ensuring they run perfectly on modern devices
            while maintaining their original gaming spirit and fun.
          </p>
          <p>
            We believe that good games transcend time. No matter how technology evolves, truly excellent game mechanics and design concepts
            will never become outdated. This is why we're committed to preserving and passing on these classic games.
          </p>
        </div>
      </div>
    </section>

    <app-footer></app-footer>
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AdBanner from '@/components/AdBanner.vue'

export default {
  name: 'HomeView',
  components: {
    AppFooter,
    AdBanner
  },
  mounted() {
    document.title = 'ArcadeJoy - 免费在线经典游戏合集 | 俄罗斯方块、贪吃蛇、飞翔小鸟'
    
    // 添加meta描述
    const metaDescription = document.createElement('meta')
    metaDescription.name = 'description'
    metaDescription.content = 'ArcadeJoy提供免费的经典街机游戏，包括俄罗斯方块、贪吃蛇、飞翔小鸟和字母冲刺。无需下载，完美适配PC和移动设备，立即开始游戏！'
    document.head.appendChild(metaDescription)
    
    // 添加关键词
    const metaKeywords = document.createElement('meta')
    metaKeywords.name = 'keywords'
    metaKeywords.content = '免费游戏,在线游戏,经典游戏,俄罗斯方块,贪吃蛇,飞翔小鸟,字母游戏,街机游戏,网页游戏'
    document.head.appendChild(metaKeywords)
  },
  methods: {
    scrollToGames() {
      document.getElementById('games').scrollIntoView({ behavior: 'smooth' })
    },
    scrollToFeatures() {
      document.getElementById('features').scrollIntoView({ behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
@import '@/assets/css/home.css';

/* 额外的样式增强 */
.hero-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 100px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-title {
  font-size: 3.5rem;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 20px;
  opacity: 0.9;
}

.hero-description {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.8;
}

.hero-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-button {
  padding: 15px 30px;
  font-size: 1.1rem;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.cta-button.primary {
  background: #4CAF50;
  color: white;
}

.cta-button.secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.cta-button.guide {
  background: #FF9800;
  color: white;
  text-decoration: none;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.floating-icons {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.icon {
  position: absolute;
  font-size: 3rem;
  opacity: 0.1;
  animation: float 6s ease-in-out infinite;
}

.tetris-icon { top: 20%; left: 10%; animation-delay: 0s; }
.snake-icon { top: 60%; left: 80%; animation-delay: 1.5s; }
.bird-icon { top: 30%; left: 85%; animation-delay: 3s; }
.alphabet-icon { top: 70%; left: 15%; animation-delay: 4.5s; }

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

.section-title {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.section-description {
  text-align: center;
  font-size: 1.1rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto 50px;
  line-height: 1.6;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.features-section {
  padding: 80px 0;
  background: #f8f9fa;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.feature-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #333;
}

.feature-card p {
  color: #666;
  line-height: 1.6;
}

.games-section {
  padding: 80px 0;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 40px;
  margin-top: 50px;
}

.game-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.game-card:hover {
  transform: translateY(-10px);
}

.game-info h4 {
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: #333;
}

.game-features {
  list-style: none;
  padding: 0;
  margin: 15px 0;
}

.game-features li {
  padding: 5px 0;
  color: #666;
  position: relative;
  padding-left: 20px;
}

.game-features li:before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #4CAF50;
  font-weight: bold;
}

.how-to-play-section {
  padding: 80px 0;
  background: #f8f9fa;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.step-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 60px;
  height: 60px;
  background: #4CAF50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 20px;
}

.faq-section {
  padding: 80px 0;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.faq-item {
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.faq-item h3 {
  color: #333;
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.faq-item p {
  color: #666;
  line-height: 1.6;
}

.about-section {
  padding: 80px 0;
  background: #f8f9fa;
}

.about-content {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
}

.about-content p {
  margin-bottom: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
  }
  
  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .games-grid {
    grid-template-columns: 1fr;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .steps-grid {
    grid-template-columns: 1fr;
  }
  
  .faq-grid {
    grid-template-columns: 1fr;
  }
}

/* 反应速度游戏样式 */
.reaction-time-thumbnail {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.reaction-preview {
  display: flex;
  gap: 20px;
  align-items: center;
}

.preview-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  position: relative;
  transition: all 0.3s ease;
}

.preview-circle.waiting {
  background: rgba(255, 255, 255, 0.2);
  animation: waiting-pulse 2s infinite;
}

.preview-circle.ready {
  background: linear-gradient(135deg, #ff4444, #cc0000);
  animation: ready-pulse 1.5s infinite;
  animation-delay: 0.5s;
}

.preview-circle.go {
  background: linear-gradient(135deg, #44ff44, #00cc00);
  animation: go-glow 1s infinite;
  animation-delay: 1s;
}

@keyframes waiting-pulse {
  0%, 100% { 
    transform: scale(1);
    opacity: 0.7;
  }
  50% { 
    transform: scale(1.1);
    opacity: 1;
  }
}

@keyframes ready-pulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 68, 68, 0.4);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(255, 68, 68, 0.6);
  }
}

@keyframes go-glow {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 rgba(68, 255, 68, 0.4);
  }
  50% { 
    transform: scale(1.2);
    box-shadow: 0 0 30px rgba(68, 255, 68, 0.8);
  }
}

.reaction-time-thumbnail:hover .preview-circle {
  transform: scale(1.1);
}

.reaction-time-thumbnail:hover .preview-circle.go {
  animation-duration: 0.5s;
}

/* 数字记忆游戏样式 */
.memory-numbers-thumbnail {
  background: linear-gradient(135deg, #9C27B0 0%, #673AB7 100%);
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.memory-preview {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.preview-number {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  animation: number-flash 2s infinite;
}

.preview-number:nth-child(1) { animation-delay: 0s; }
.preview-number:nth-child(2) { animation-delay: 0.5s; }
.preview-number:nth-child(3) { animation-delay: 1s; }
.preview-number:nth-child(4) { animation-delay: 1.5s; }

@keyframes number-flash {
  0%, 80%, 100% { 
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1);
  }
  10%, 70% { 
    background: rgba(255, 215, 0, 0.8);
    transform: scale(1.1);
    color: #000;
  }
}

/* 打字竞速游戏样式 */
.typing-race-thumbnail {
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.typing-preview {
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.9);
  padding: 15px 20px;
  border-radius: 10px;
  color: #333;
  font-family: 'Courier New', monospace;
}

.preview-text {
  font-size: 1.2rem;
}

.preview-cursor {
  font-size: 1.5rem;
  color: #2196F3;
  animation: cursor-blink 1s infinite;
}

@keyframes cursor-blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* 数独游戏样式 */
.sudoku-thumbnail {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.sudoku-preview {
  background: white;
  padding: 10px;
  border-radius: 8px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  width: 90px;
  height: 90px;
}

.preview-cell {
  background: #f0f0f0;
  border: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;
  color: #333;
}

.preview-cell:not(:empty) {
  background: #e3f2fd;
  color: #1976d2;
}

/* 颜色匹配游戏样式 */
.color-match-thumbnail {
  background: linear-gradient(135deg, #E91E63 0%, #C2185B 100%);
  position: relative;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  gap: 20px;
}

.color-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.preview-target {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #FF5722, #FF9800);
  border: 3px solid white;
  animation: target-glow 2s infinite;
}

@keyframes target-glow {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
  }
}

.preview-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.preview-option {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

.preview-option.color1 { background: linear-gradient(45deg, #FF5722, #FF9800); }
.preview-option.color2 { background: linear-gradient(45deg, #4CAF50, #8BC34A); }
.preview-option.color3 { background: linear-gradient(45deg, #2196F3, #03A9F4); }
.preview-option.color4 { background: linear-gradient(45deg, #9C27B0, #E91E63); }

.color-match-thumbnail:hover .preview-option {
  transform: scale(1.1);
}

/* 2048游戏样式 */
.game-2048-thumbnail {
  background: linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%);
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.game-2048-preview {
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-board {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #2d3436, #636e72);
  padding: 5px;
  border-radius: 8px;
  border: 2px solid rgba(116, 185, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.preview-tile {
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  transition: all 0.3s ease;
}

.preview-tile.empty {
  background: linear-gradient(135deg, #74b9ff, #0984e3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

.preview-tile.tile-2 { background: linear-gradient(135deg, #f8f9fa, #e9ecef); color: #495057; font-weight: 700; }
.preview-tile.tile-4 { background: linear-gradient(135deg, #fff3cd, #ffeaa7); color: #856404; font-weight: 700; }
.preview-tile.tile-8 { background: linear-gradient(135deg, #ff9f43, #ee5a24); color: #ffffff; font-weight: 700; }
.preview-tile.tile-16 { background: linear-gradient(135deg, #ff6b6b, #ee5253); color: #ffffff; font-weight: 700; }
.preview-tile.tile-32 { background: linear-gradient(135deg, #ff4757, #c44569); color: #ffffff; font-weight: 700; }
.preview-tile.tile-64 { background: linear-gradient(135deg, #5f27cd, #341f97); color: #ffffff; font-weight: 700; }
.preview-tile.tile-128 { background: linear-gradient(135deg, #00d2d3, #01a3a4); color: #ffffff; font-size: 0.6rem; font-weight: 700; }
.preview-tile.tile-256 { background: linear-gradient(135deg, #0984e3, #006ba6); color: #ffffff; font-size: 0.6rem; font-weight: 700; }
.preview-tile.tile-512 { background: linear-gradient(135deg, #6c5ce7, #5f3dc4); color: #ffffff; font-size: 0.6rem; font-weight: 700; }
.preview-tile.tile-1024 { background: linear-gradient(135deg, #fd79a8, #e84393); color: #ffffff; font-size: 0.5rem; font-weight: 700; }
.preview-tile.tile-2048 { background: linear-gradient(135deg, #fdcb6e, #e17055); color: #ffffff; font-size: 0.5rem; font-weight: 700; }

.preview-tile.highlight {
  animation: tile-glow 2s infinite;
  box-shadow: 0 0 10px rgba(237, 194, 46, 0.8);
}

@keyframes tile-glow {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 10px rgba(237, 194, 46, 0.8);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(237, 194, 46, 1);
  }
}

.game-2048-thumbnail:hover .preview-tile:not(.empty) {
  transform: scale(1.05);
}

.game-2048-thumbnail:hover .preview-tile.highlight {
  animation-duration: 1s;
}
</style>